import {
 createContext,
 ReactNode,
 useCallback,
 useContext,
 useEffect,
 useMemo,
 useState,
} from 'react'

import {
 ACCESS_TOKEN_KEY,
 MIN_TOKEN_VALIDITY_MINUTE,
 REFRESH_TOKEN_KEY,
} from 'constants/common'
import fetchTokenFromRefreshToken from 'utils/api/auth/fetchTokenFromRefreshToken'
import {
 clearSessionAndRedirect,
 handleAuthFailure,
} from 'utils/auth/sessionManager'
import {getCookieValue, persistToken} from 'utils/helper'
import parseJWT from 'utils/parseToken'
import IAuth from 'models/Auth'
import * as authHelper from 'utils/helpers/authHelper'

interface AuthContextProps {
 userData: IAuth | undefined
 isAuthenticated: boolean
 isAuthReady: boolean
 persistTokens: VoidFunction
 logout: VoidFunction
}

export const AuthContext = createContext<AuthContextProps>({
 userData: {
  auth: {
   accessExpiresIn: 0,
   accessToken: '',
   refreshExpiresIn: 0,
   refreshToken: '',
  },
  categoryId: '',
  companyId: '',
  companyTypeId: '',
  email: '',
  mobileNumber: '',
  name: '',
  state: '',
  district: '',
  block: '',
  userCategoryId: '',
  userCategoryKey: '',
  userId: 0,
  userType: '',
  organisation: '',
  status: '',
  userCategory: '',
  category: '',
  logo: '',
  authId: '',
  selectedLanguage: 'en',
  menu: [],
  routes: [],
  userModule: {},
  userSubModule: {},
 },
 isAuthenticated: false,
 isAuthReady: false,
 logout: () => undefined,
 persistTokens: () => undefined,
})

const useAuth = () => {
 return useContext(AuthContext)
}

interface Props {
 children: ReactNode
}

const getTokenExpiryTime = (token: string | null) => {
 if (token) {
  const {exp} = parseJWT(token)
  return new Date(exp * 1000).getTime()
 }
 return null
}

const checkTokenExpiry = (
 tokenExpiryTime: number | null,
 minValidity = 60000
) => {
 if (tokenExpiryTime != null) {
  const currentTime = new Date().getTime()
  return tokenExpiryTime - currentTime <= minValidity
 }
 return false
}

const isTokenExpired = (token: string | null) => {
 const expiryTime = getTokenExpiryTime(token)
 if (expiryTime == null) {
  return true
 }
 return expiryTime <= new Date().getTime()
}

const minValidity = MIN_TOKEN_VALIDITY_MINUTE * 60 * 1000

const AuthProvider = ({children}: Props) => {
 const [userData, setuserData] = useState<
  IAuth | undefined
 >()
 const [accessToken, setAccessToken] = useState<
  string | null
 >(null)
 const [refreshToken, setRefreshToken] = useState<
  string | null
 >(null)
 const [isAuthReady, setIsAuthReady] = useState(false)
 const tabId = Math.random()

 const accessTokenExpiryTime = useMemo(
  () => getTokenExpiryTime(accessToken),
  [accessToken]
 )

 const refreshTokenExpiryTime = useMemo(
  () => getTokenExpiryTime(refreshToken),
  [refreshToken]
 )

 const logout = useCallback(() => {
  setAccessToken(null)
  setRefreshToken(null)
  clearSessionAndRedirect()
 }, [])

 const updateToken = () => {
  const accessTokenNew =
   getCookieValue(ACCESS_TOKEN_KEY) ?? null
  const refreshTokenNew =
   getCookieValue(REFRESH_TOKEN_KEY) ?? null
  if (refreshTokenNew !== refreshToken) {
   setAccessToken(accessTokenNew)
   setRefreshToken(refreshTokenNew)
   return false
  } else {
   return true
  }
 }

 const refreshTokenHandler = useCallback(
  async () => {
   if (!localStorage) {
    return
   }
   const tokenExpiry = minValidity
   if (
    checkTokenExpiry(accessTokenExpiryTime, tokenExpiry) ||
    checkTokenExpiry(refreshTokenExpiryTime, tokenExpiry)
   ) {
    if (!updateToken()) {
     return false
    }
    const token = getCookieValue(REFRESH_TOKEN_KEY) ?? null
    if (!localStorage.getItem('tabId')) {
     localStorage.setItem('tabId', tabId.toString())
    }
    if (
     token &&
     localStorage.getItem('tabId') === tabId.toString()
    ) {
     const response = await fetchTokenFromRefreshToken(
      token,
      {silent: true}
     )
     if (response?.auth?.accessToken) {
      persistToken(
       ACCESS_TOKEN_KEY,
       response.auth?.accessToken,
       response.auth?.accessExpiresIn
      )
      persistToken(
       REFRESH_TOKEN_KEY,
       response.auth?.refreshToken,
       response.auth?.refreshExpiresIn
      )
      setAccessToken(response.auth?.accessToken || null)
      setRefreshToken(response.auth?.refreshToken || null)
     } else {
      handleAuthFailure()
     }

     localStorage.removeItem('tabId')
    }
   }
  },
  // eslint-disable-next-line react-hooks/exhaustive-deps
  [accessTokenExpiryTime, refreshTokenExpiryTime]
 )

 const persistTokens = useCallback(() => {
  setIsAuthReady(false)
  const accessTokenValue =
   getCookieValue(ACCESS_TOKEN_KEY) ?? null
  const refreshTokenValue =
   getCookieValue(REFRESH_TOKEN_KEY) ?? null

  if (
   accessTokenValue &&
   refreshTokenValue &&
   isTokenExpired(accessTokenValue) &&
   isTokenExpired(refreshTokenValue)
  ) {
   handleAuthFailure()
   return
  }

  setAccessToken(accessTokenValue)
  setRefreshToken(refreshTokenValue)
  setuserData(authHelper.getAuth())
  setIsAuthReady(true)
 }, [])

 useEffect(() => {
  persistTokens()
 }, [persistTokens])

 useEffect(() => {
  let timer: number
  if (refreshToken) {
   // console.log("In Refresh token use effect", refreshToken)
   void refreshTokenHandler()
   timer = window.setInterval(
    async () => refreshTokenHandler(),
    2 * 60000
   )
  }
  return () => {
   if (timer) {
    window.clearInterval(timer)
   }
  }
 }, [refreshToken, refreshTokenHandler])

 const handleTabClosing = () => {
  localStorage.removeItem('tabId')
 }

 useEffect(() => {
  localStorage.removeItem('tabId')
  window.addEventListener('unload', handleTabClosing)
  return () => {
   window.removeEventListener('unload', handleTabClosing)
  }
 }, [])

 return (
  <AuthContext.Provider
   value={{
    userData,
    isAuthenticated: !!accessToken && !!refreshToken,
    isAuthReady,
    logout,
    persistTokens,
   }}
  >
   {children}
  </AuthContext.Provider>
 )
}

export {useAuth, AuthProvider}
